import { Component, inject, ChangeDetectionStrategy, input, OnDestroy, computed } from '@angular/core';
import { Store } from '@ngrx/store';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { UserEntity } from '@zerops/zerops/core/user-base';
import { ZefError } from '@zerops/zef/errors';
import { Subject } from 'rxjs';
import { map, withLatestFrom, take, filter } from 'rxjs/operators';
import { sudoReauthActions } from './sudo-reauth.state';
import { SudoReauthForm } from './sudo-reauth.form';

interface AuthMethodOption {
  type: 'password' | 'totp' | 'recovery' | 'webauthn';
  icon: string;
  label: string;
}

/**
 * Sudo Re-authentication Feature Component
 *
 * Presentation component that:
 * - Renders authentication UI (password, TOTP, WebAuthn)
 * - Dispatches user action events to effects
 * - Displays state derived from store
 *
 * Business logic handled by SudoReauthEffect.
 */
@Component({
  selector: 'z-sudo-reauth-feature',
  templateUrl: './sudo-reauth.feature.html',
  styleUrl: './sudo-reauth.feature.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SudoReauthFeature extends ZefReactiveComponent implements OnDestroy {
  // # Deps
  #store = inject(Store);
  #form = inject(SudoReauthForm);
  #userEntity = inject(UserEntity);

  // # Inputs
  error = input.required<ZefError>();

  // # Computed
  availableMethods = computed<AuthMethodOption[]>(() => {
    const user = this.state.userInfo;
    const methods: AuthMethodOption[] = [];

    if (user?.hasU2F || user?.hasPasskey) {
      methods.push({ type: 'webauthn', icon: 'fingerprint', label: 'Security Key' });
    }

    if (user?.totpIsSet) {
      methods.push({ type: 'totp', icon: 'smartphone', label: 'Authenticator' });
    }

    if (user?.passwordIsSet) {
      methods.push({ type: 'password', icon: 'lock', label: 'Password' });
    }

    if (user?.totpIsSet || user?.hasU2F || user?.hasPasskey) {
      methods.push({ type: 'recovery', icon: 'healing', label: 'Recovery' });
    }

    return methods;
  });

  // # Event Streams
  onCancel$ = new Subject<void>();
  onSubmit$ = new Subject<void>();
  onAuthMethodChange$ = new Subject<'password' | 'totp' | 'recovery' | 'webauthn'>();

  // # Data
  // -- sync
  submitPasswordKey = sudoReauthActions.submitPassword.type;
  submitTOTPKey = sudoReauthActions.submitTOTP.type;
  startWebAuthnKey = sudoReauthActions.startWebAuthn.type;

  // -- async
  userInfo$ = this.#userEntity.activeUser$;
  formState$ = this.#form.state$;

  // # State resolver
  state = this.$connect({
    userInfo: this.userInfo$,
    formState: this.formState$
  });

  // # Dispatcher
  constructor() {
    super();

    this.$dispatchActions([
      // Initialize authMethod to first available method on mount
      this.#userEntity.activeUser$.pipe(
        take(1),
        map((user) => {
          // Determine first available auth method
          let firstMethod: 'password' | 'totp' | 'recovery' | 'webauthn' = 'password';

          if (user?.hasU2F || user?.hasPasskey) {
            firstMethod = 'webauthn';
          } else if (user?.totpIsSet) {
            firstMethod = 'totp';
          } else if (user?.passwordIsSet) {
            firstMethod = 'password';
          }

          return this.#form.setValue('authMethod', firstMethod);
        })
      ),

      // Auto-start WebAuthn on mount if user has hardware key
      // Initiated from component (not effect) because this is an inline error UI
      // that mounts/unmounts with error visibility
      this.#userEntity.activeUser$.pipe(
        take(1),
        filter((user) => !!(user?.hasU2F || user?.hasPasskey)),
        map(() => sudoReauthActions.startWebAuthn())
      ),

      this.onCancel$.pipe(
        map(() => sudoReauthActions.cancel())
      ),

      // Form submit - handles password, TOTP, and recovery (methods with inputs)
      this.onSubmit$.pipe(
        withLatestFrom(this.#form.value$),
        map(([ _, formValue ]) => {
          switch (formValue.authMethod) {
            case 'password':
              return sudoReauthActions.submitPassword({
                data: { password: formValue.password }
              });

            case 'totp':
              return sudoReauthActions.submitTOTP({
                data: { token: formValue.totpCode }
              });

            case 'recovery':
              return sudoReauthActions.submitTOTP({
                data: { token: formValue.recoveryCode }
              });

            case 'webauthn':
              return sudoReauthActions.startWebAuthn();

            default:
              return sudoReauthActions.cancel();
          }
        })
      ),

      // Auth method change - update form value
      this.onAuthMethodChange$.pipe(
        map((method) => this.#form.setValue('authMethod', method))
      )
    ]);
  }

  ngOnDestroy() {
    this.#store.dispatch(sudoReauthActions.componentDestroyed());
    super.ngOnDestroy();
  }

}
